// src/features/capacity/CapacityPage.jsx
//
// PLACES-CAPACITY-UNIFIED — the Places > Capacity lens. Replaces the Pressure
// and Scenario collection lenses (kept unrouted for rollback). All shareable
// state lives in the URL (capacityUrl.js); router navigation owns history so
// browser Back/Forward replays scenario, view and panel changes.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { SidePanelHost, SidePanelRailButton } from "../../components/SidePanelRail.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import "./capacityKpis.css";
import { useScenarioPanelPreference } from "./useScenarioPanelPreference.js";
import { T } from "../../app/tokens.js";
import { PAGE_CONTENT_MAX_WIDTH, PAGE_CONTENT_PADDING } from "../../components/PageHeaderStack.jsx";
import { getCapacity, getPlan } from "../../data/marketsClient.js";
import { getEffectiveSpacePolicy, SPACE_POLICY_FALLBACK_NOTICE } from "../plan/policyClient.js";
import { useSpacePolicy } from "../../services/spacePolicyStore.js";
import { computePortfolioKpis, selectActiveMarketsForKpis } from "../plan/marketsService.js";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { fmtKpiUsd } from "../../utils/fmtAbbreviated.js";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import { ConsolidatedFilter } from "../../v2/components/ConsolidatedFilter.jsx";
import { SortControl } from "../../v2/components/SortControl.jsx";
import { LIST_PRESENTATION } from "../../v2/components/listPresentation.js";
import { ToolbarViewToggle } from "../../v2/components/ToolbarViewToggle.jsx";
import { ToolbarAddButton } from "../../v2/components/ToolbarAddButton.jsx";
import { CollectionCount } from "../../v2/components/InlineFilterToolbar.jsx";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { allOptionValues, withFacetCounts } from "../../utils/checkedSelections.js";
import { activeFilterGroups } from "../../utils/filterHiddenSearch.js";
import { capacityCheckedSelections, capacityPredicateState, capacitySelectionPatch } from "./capacityFilters.js";
import { showToast } from "../../components/toastService.jsx";
import {
  DEFAULT_TARGET_BUFFER_PCT, NEUTRAL_LEVERS, STATUS_LABEL, STATUS_RANK, buildPeriods, changedLevers,
  deltaTone, evaluateGrid, isScenarioActive, monthKey, monthLabel, periodIndexForFocus, scenarioResult, summarize,
  rowSummary, compareByIssue,
} from "./capacityModel.js";
import { parseCapacityState, serializeCapacityState, STATUS_FILTERS, DEFAULT_SORT, DEFAULT_SORT_DIR } from "./capacityUrl.js";
import { CapacityGrid, nameColumnWidth } from "./CapacityGrid.jsx";
import { CapacityScenarioPanel } from "./CapacityScenarioPanel.jsx";
import { CapacityViewKey } from "./CapacityViewKey.jsx";
import { CapacityAddMarketDialog } from "./CapacityAddMarketDialog.jsx";
import { TONE_COLOR, INK } from "./capacityStyles.js";
import { FORECAST_FADE_LABEL } from "./capacityGridStyles.js";
import { PartialMarker } from "../../components/PartialTotal.jsx";
import { coverageText } from "../../services/seatAccounting.js";

const SORT_FIELDS = [
  { value: "issue", label: "First issue" },
  { value: "market", label: "Market" },
  { value: "people", label: "Demand", numeric: true },
  { value: "seats", label: "Usable seats", numeric: true },
  { value: "pressure", label: "Pressure at focus", numeric: true },
];
const SHORT_LABEL_BELOW = 1180;

// Pure: filter + sort the evaluated rows. Exported for tests.
export function selectRows(rows, { q, region, status, regionNone, statusNone, sort, dir }, focusIdx, scenarioOn) {
  const at = (r) => (scenarioOn ? r.scenario : r.baseline)[focusIdx];
  let out = rows.filter((r) => !regionNone && !statusNone && (!q || matchesFoldedQuery(q, r.market.name ?? "", r.market.region ?? ""))
    && (!region.length || region.includes(r.market.region))
    && (!status.length || status.includes(at(r)?.status)));
  const val = {
    market: (r) => (r.market.name ?? "").toLowerCase(),
    people: (r) => r.market.totalPeople ?? -1,
    seats: (r) => r.market.totalUsableSeats ?? -1,
    pressure: (r) => STATUS_RANK[at(r)?.status] * 1e6 + (Number.isFinite(at(r)?.ratio) ? at(r).ratio : 0),
  }[sort];
  if (!val) {
    // Default: same derived result as the FIRST ISSUE column (series + horizon).
    const sum = new Map(out.map((r) => [r, rowSummary(r, scenarioOn)]));
    return [...out].sort((a, b) => (dir === "desc" ? -1 : 1) * compareByIssue(a, b, sum.get(a), sum.get(b))
      || String(a.market.id).localeCompare(String(b.market.id)));
  }
  out = [...out].sort((a, b) => {
    const x = val(a), y = val(b);
    const c = x < y ? -1 : x > y ? 1 : 0;
    return (dir === "desc" ? -c : c) || (a.market.name ?? "").localeCompare(b.market.name ?? "")
      || String(a.market.id).localeCompare(String(b.market.id));
  });
  return out;
}

// Capacity content adapter only; shared tiles own full/condensed presentation.
// sub = visible context; detail = full focus/population explanation.
function Kpi({ testId, label, today, scenario, tone, sub, detail, isFiltered, coverage }) {
  const comparison = scenario !== undefined;
  return (
    <KpiTile testId={testId} label={label}
      value={comparison ? <><span data-testid={`${testId}-today`} style={{ color: T.light, fontSize: T.fsSm }}>{today}</span><span style={{ color: T.light, fontSize: T.fsSm }}> → </span><span data-testid={`${testId}-scenario`}>{scenario}</span></> : <span data-testid={`${testId}-value`}>{today}</span>}
      valueStyle={{ marginTop: 4, lineHeight: 1.25,
        ...(comparison ? { flexWrap: "wrap", whiteSpace: "normal", fontSize: T.fsLg, columnGap: 4, rowGap: 4 } : {}) }}
      color={comparison && tone !== "flat" ? TONE_COLOR[tone] : T.dark}
      descriptor={coverage?.missing?.length ? <span>{sub}<PartialMarker coverage={coverage} measure="demand" /></span> : sub}
      tooltip={`${comparison ? `Baseline: ${today}. Scenario: ${scenario}. ` : ""}${detail ?? ""} ${coverageText(coverage, "demand")}`}
      isFiltered={isFiltered} scope={testId === "capacity-kpi-cost" ? "portfolio" : "responsive"} />
  );
}

function Skeleton() {
  return (
    <div data-testid="capacity-loading" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} style={{ height: 28, borderRadius: 6, background: T.lightGray, opacity: 1 - i * 0.09 }} />
      ))}
    </div>
  );
}

export function CapacityPage({ currentRole = "viewer", userId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { canSee } = useSensitivityPolicy();
  const canSeeCost = canSee("market_plan.rent_cost");
  const canEdit = currentRole === "editor" || currentRole === "admin";
  const state = useMemo(() => parseCapacityState(location.search), [location.search]);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planError, setPlanError] = useState(null);
  const livePolicy = useSpacePolicy();
  const policy = livePolicy.baseline;
  const policyError = livePolicy.error ?? null;
  const [adding, setAdding] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const panelPreference = useScenarioPanelPreference(userId);
  const panelOpen = state.panel ?? panelPreference.open;
  const [compact, setCompact] = useState(false);
  const toolbarRef = useRef(null);

  // Canonicalise invalid/retired params once per URL, without a history entry.
  useEffect(() => {
    const canonical = serializeCapacityState(state);
    if (canonical !== location.search && !(canonical === "" && location.search === "?")) {
      navigate({ pathname: location.pathname, search: canonical, hash: location.hash }, { replace: true, state: location.state });
    }
  }, [state, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback((patch, { replace = false } = {}) => {
    const next = serializeCapacityState({ ...parseCapacityState(location.search), ...patch });
    navigate({ pathname: location.pathname, search: next, hash: location.hash }, { replace });
  }, [location.search, location.pathname, location.hash, navigate]);
  const setPanelOpen = (open) => {
    panelPreference.save(open);
    update({ panel: open });
  };

  const load = useCallback(() => {
    setError(null); setData(null);
    getCapacity().then(setData).catch((e) => setError(e?.message ?? "Failed to load capacity."));
    setPlanError(null);
    getPlan().then((response) => {
      if (!Array.isArray(response?.markets)) throw new Error("Invalid plan response: markets must be an array.");
      setPlan(response.markets);
    }).catch((e) => setPlanError(e?.message ?? "Failed to load cost."));
  }, []);
  useEffect(() => { load(); }, [load]);

  useLayoutEffect(() => {
    const node = toolbarRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setNarrow(node.getBoundingClientRect().width < SHORT_LABEL_BELOW));
    ro.observe(node);
    return () => ro.disconnect();
  }, [data]);

  const nearLimit = livePolicy.baseline?.nearLimitPct ?? (100 - livePolicy.baseline?.hcBufferPct);
  // Compatibility parameter for the existing ladder, not Target buffer planning math.
  const policyBuffer = Number.isFinite(nearLimit) ? 100 - nearLimit : null;
  const bufferPct = policyBuffer ?? DEFAULT_TARGET_BUFFER_PCT;
  const bufferNote = policyBuffer != null ? null : SPACE_POLICY_FALLBACK_NOTICE;
  const months = useMemo(() => data?.months ?? [], [data]);
  const periods = useMemo(() => buildPeriods(months, state.view), [months, state.view]);
  const focusKey = state.focus ?? monthKey(months[0]?.start);
  const focusIdx = periodIndexForFocus(periods, months, focusKey);
  const scenarioOn = isScenarioActive(state.levers);
  const allRows = useMemo(() => evaluateGrid(data?.markets ?? [], periods, months, state.levers, bufferPct),
    [data, periods, months, state.levers, bufferPct]);
  const regions = useMemo(() => [...new Set((data?.markets ?? []).map((m) => m.region).filter(Boolean))].sort(), [data]);
  const filterDimensions = useMemo(() => [
    { id: "region", label: "Region", options: regions.map((r) => ({ value: r, label: r })) },
    { id: "status", label: "Status at focus", options: STATUS_FILTERS.map((s) => ({ value: s, label: STATUS_LABEL[s] })) },
  ], [regions]);
  const baselineSelections = useMemo(() => Object.fromEntries(filterDimensions.map(dim => [dim.id, allOptionValues(dim)])), [filterDimensions]);
  const groupDefaultStates = { region: !state.regionNone && !state.region.length };
  const selections = useMemo(() => capacityCheckedSelections(state, filterDimensions), [state, filterDimensions]);
  const rows = useMemo(() => selectRows(allRows, capacityPredicateState(state, filterDimensions, selections), focusIdx, scenarioOn),
    [allRows, state, filterDimensions, selections, focusIdx, scenarioOn]);
  const dimensions = useMemo(() => withFacetCounts(filterDimensions, selections,
    allRows.map(row => ({ ...row, id: row.market.id })),
    (candidates, selected, group) => selectRows(candidates,
      capacityPredicateState(state, filterDimensions, selected, group), focusIdx, scenarioOn)),
  [filterDimensions, selections, allRows, state, focusIdx, scenarioOn]);
  const today = useMemo(() => summarize(rows, periods, focusIdx, "baseline"), [rows, periods, focusIdx]);
  const scen = useMemo(() => (scenarioOn ? summarize(rows, periods, focusIdx, "scenario") : null), [rows, periods, focusIdx, scenarioOn]);
  const demandCoverage = useMemo(() => {
    const sources = rows.map(r => r.market.months?.[0]?.demandCoverage).filter(Boolean);
    return { known: sources.reduce((s, c) => s + c.known, 0),
      total: sources.reduce((s, c) => s + c.total, 0),
      missing: [...new Map(sources.flatMap(c => c.missing).map(o => [o.id, o])).values()] };
  }, [rows]);
  const allDemandUnknown = rows.length > 0 && rows.every(r => r.current?.baseline?.demand == null);
  const allSpareUnknown = rows.length > 0 && rows.every(r => r.spareCurrent?.baseline?.demand == null);
  const spareCoverage = useMemo(() => {
    const sources = rows.map(r => (r.market.spareBaselineMonth ?? r.market.months?.[0])?.demandCoverage).filter(Boolean);
    return { known: sources.reduce((s, c) => s + c.known, 0),
      total: sources.reduce((s, c) => s + c.total, 0),
      missing: [...new Map(sources.flatMap(c => c.missing).map(o => [o.id, o])).values()] };
  }, [rows]);
  const countedMarket = r => !["excluded", "no_seats"].includes(r.current?.baseline?.status);
  const countedRows = rows.filter(countedMarket).length;
  const countedTotal = allRows.filter(countedMarket).length;
  const displayDemandMetric = n => allDemandUnknown ? "–" : n;
  const result = scen ? scenarioResult(scen) : null;

  // Annual cost keeps Plan's portfolio scope, financial gate and missing-cost semantics.
  const cost = useMemo(() => (plan ? computePortfolioKpis(selectActiveMarketsForKpis(plan)) : null), [plan]);

  const groupsFiltered = activeFilterGroups(filterDimensions, selections, baselineSelections, true).length > 0
    || state.region.length > 0 || state.regionNone || state.statusNone;
  const hasFilters = !!state.q || groupsFiltered
    || state.sort !== DEFAULT_SORT || state.dir !== DEFAULT_SORT_DIR;
  const focus = periods[focusIdx];
  const kpiFiltered = !!state.q || groupsFiltered;

  const setFocus = (i, toggleMarketId = null) => {
    const p = periods[i];
    if (!p) return;
    const curIdx = months.findIndex((m) => monthKey(m.start) === focusKey);
    const key = p.monthIdxs.includes(curIdx) ? focusKey : monthKey(months[p.monthIdxs[0]].start);
    // A cell both focuses and toggles. Keep those changes in one router
    // update; two navigations derived from the same old URL lose the focus.
    update({
      focus: key === monthKey(months[0]?.start) ? null : key,
      ...(toggleMarketId ? { open: state.open.includes(toggleMarketId) ? state.open.filter(id => id !== toggleMarketId) : [...state.open, toggleMarketId] } : {}),
    }, { replace: true });
  };
  const toggleOpen = (id) => update({ open: state.open.includes(id) ? state.open.filter((x) => x !== id) : [...state.open, id] }, { replace: true });

  const pair = (a, b, higherIsBetter = false, fmt = String) => (scenarioOn
    ? { today: fmt(a), scenario: fmt(b), tone: deltaTone(a, b, higherIsBetter) }
    : { today: fmt(a) });

  return (
    <div data-testid="capacity-page" style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0, maxWidth: PAGE_CONTENT_MAX_WIDTH, padding: PAGE_CONTENT_PADDING, margin: "0 auto", width: "100%", boxSizing: "border-box", paddingBottom: 40 }}>
      <KpiBand testId="capacity-kpis" ariaLabel="Capacity metrics">
        <Kpi isFiltered={kpiFiltered} testId="capacity-kpi-markets" label="Markets" today={data ? `${countedRows} of ${countedTotal}` : "\u2014"} detail="Markets in view of active, non-exited markets; excludes No active office and No seats" />
        <Kpi coverage={demandCoverage} isFiltered={kpiFiltered} testId="capacity-kpi-over" label="Over limit" {...pair(today.over, scen?.over, false, n => String(displayDemandMetric(n)))} sub="in view" detail={focus ? `Markets Over limit at ${focus.full} vs usable seats (attendance-adjusted)` : "Assigned today this month, forecast from next month vs usable seats (attendance-adjusted)"} />
        <Kpi coverage={demandCoverage} isFiltered={kpiFiltered} testId="capacity-kpi-tight" label="Near limit" {...pair(today.tightOrDensify, scen?.tightOrDensify, false, n => String(displayDemandMetric(n)))} sub="in view" detail={focus ? `Markets Near limit at ${focus.full} vs usable seats (attendance-adjusted)` : "Near limit vs usable seats (attendance-adjusted)"} />
        <Kpi coverage={spareCoverage} isFiltered={kpiFiltered} testId="capacity-kpi-spare" label={`Spare usable seats \u00B7 ${monthLabel(months[0]?.start)}`} {...pair(today.spare, scen?.spare, true, (n) => (allSpareUnknown || n == null ? "\u2014" : Math.round(n).toLocaleString()))} sub="in view" detail={`Gross positive-only spare: original forecast demand vs usable seats across markets in view at ${monthLabel(months[0]?.start)}; not the assigned-today status calculation`} />
        <Kpi isFiltered={kpiFiltered} testId="capacity-kpi-wall" label="Next wall" {...(scenarioOn
          ? { today: today.wallCompact ?? "None", scenario: scen.wallCompact ?? "None", tone: today.wallIdx === scen.wallIdx ? "flat" : (scen.wallIdx == null || (today.wallIdx != null && scen.wallIdx > today.wallIdx)) ? "good" : "bad" }
          : { today: today.wallCompact ?? "None" })} sub="in view" detail={`First period any market in view is over: ${today.wallLabel ?? "None"}${scenarioOn ? `. Scenario: ${scen.wallLabel ?? "None"}` : ""}`} />
        {canSeeCost && <Kpi testId="capacity-kpi-cost" label="Annual cost"
          today={planError ? "Unavailable" : cost?.totalAnnualCost != null ? fmtKpiUsd(cost.totalAnnualCost) : "\u2014"}
          sub={planError ? "error" : cost?.annualCostPartial ? "partial cost" : "portfolio"}
          detail={planError ? planError : `Portfolio, all active markets${cost?.annualCostPartial ? " \u00B7 partial cost coverage" : ""}`} />}
      </KpiBand>

      <div ref={toolbarRef} data-testid="capacity-toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: T.toolbarRowH, flexWrap: "wrap", minWidth: 0 }}>
        <div data-testid="capacity-toolbar-left" style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flexWrap: "wrap" }}>
          <SearchInput value={state.q} onChange={(q) => update({ q }, { replace: true })} placeholder={"Search markets\u2026"} testId="capacity-search" style={{ width: narrow ? 130 : 170 }} />
          <ConsolidatedFilter testId="capacity-filter" dimensions={dimensions} selections={selections}
            checkedInclusion unifiedDefaults baselineSelections={baselineSelections} groupDefaultStates={groupDefaultStates}
            onResetDefaults={(group) => update(group
              ? { [group]: [], [`${group}None`]: false }
              : { region: [], status: [], regionNone: false, statusNone: false })}
            onChange={(sel) => update(capacitySelectionPatch(filterDimensions, selections, sel))} />
          <SortControl testId="capacity-sort" showEffectiveSort showSecondary={false} fields={SORT_FIELDS} defaultKey={DEFAULT_SORT}
            primary={{ key: state.sort, dir: state.dir }} secondary={{ key: null, dir: "asc" }}
            onChange={({ primary }) => update({ sort: primary.key ?? DEFAULT_SORT, dir: primary.dir })} />
          <ToolbarViewToggle testId="capacity-view-toggle" ariaLabel="Period view" value={state.view}
            onChange={(view) => update({ view })}
            segments={[
              { key: "monthly", label: "Months", testid: "capacity-view-monthly" },
              { key: "quarterly", label: "Quarters", testid: "capacity-view-quarterly" },
            ]} />
          {hasFilters && (
            <button type="button" data-testid="capacity-clear" onClick={() => update({ q: "", region: [], status: [], regionNone: false, statusNone: false, sort: DEFAULT_SORT, dir: DEFAULT_SORT_DIR })}
              style={{ background: "transparent", border: 0, color: "var(--v2-accent)", fontSize: T.fsBase, fontWeight: T.fwBold, cursor: "pointer", padding: "7px 9px", fontFamily: "inherit" }}>Clear</button>
          )}
        </div>
        <div data-testid="capacity-toolbar-right" style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: "auto" }}>
          <button type="button" data-testid="capacity-compact" aria-pressed={compact} onClick={() => setCompact((v) => !v)}
            style={{ border: `1px solid ${T.border}`, borderRadius: 6, background: compact ? T.lightGray : T.card, color: T.dark, fontSize: T.fsXs, fontFamily: "inherit", padding: "5px 8px", cursor: "pointer" }}>Compact</button>
          <button type="button" data-testid="capacity-scenario-toggle" aria-pressed={panelOpen} onClick={() => setPanelOpen(!panelOpen)}
            style={{ height: 28, padding: "0 12px", borderRadius: 999, border: `1px solid ${panelOpen || scenarioOn ? T.blueSoftBorder : T.border}`, background: panelOpen || scenarioOn ? T.blueSoft : T.card, color: panelOpen || scenarioOn ? T.blue : T.dark, fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
            Scenario{scenarioOn ? " \u00B7 on" : ""}
          </button>
          <CapacityViewKey view={state.view} bufferPct={bufferPct} />
          {canEdit && <ToolbarAddButton testId="capacity-add-market" onClick={() => setAdding(true)}>+ Add market</ToolbarAddButton>}
        </div>
      </div>
      {data && <CollectionCount testId="capacity-count" count={countedRows} total={countedTotal} />}
      {panelPreference.error && <div role="alert" style={{ color: T.ragRed, fontSize: T.fsXs }}>
        Scenario panel choice could not be loaded or saved: {panelPreference.error}{" "}
        <button type="button" onClick={panelPreference.retry}>Retry scenario panel preference</button>
      </div>}

      {bufferNote && (
        <div data-testid="capacity-policy-fallback" role="status" style={{ fontSize: T.fsXs, color: T.ragAmberText, background: T.ragAmberBg, border: `1px solid ${T.ragAmberBorder}`, borderRadius: 8, padding: "5px 10px" }}>{bufferNote}</div>
      )}
      {scenarioOn && (
        <div data-testid="capacity-scenario-banner" role="status" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", background: T.blueSoft, border: `1px solid ${T.blueSoftBorder}`, borderRadius: 10, padding: "7px 12px" }}>
          <b style={{ fontSize: T.fsSm, color: T.blue }}>Scenario on</b>
          {changedLevers(state.levers).map((c) => (
            <span key={c.key} data-testid={`capacity-banner-chip-${c.key}`} style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, background: T.card, border: `1px solid ${T.blueSoftBorder}`, borderRadius: 999, padding: "2px 9px" }}>{c.text}</span>
          ))}
          <span style={{ fontSize: T.fsXs, color: T.mid }}>Outlined squares changed status.</span>
          <button type="button" data-testid="capacity-banner-reset" onClick={() => update({ levers: { ...NEUTRAL_LEVERS } })}
            style={{ marginLeft: "auto", border: 0, background: "transparent", color: T.blue, fontSize: T.fsSm, fontWeight: T.fwBold, cursor: "pointer", fontFamily: "inherit" }}>Reset</button>
        </div>
      )}

      {error && (
        <div role="alert" data-testid="capacity-error" style={{ display: "flex", alignItems: "center", gap: 12, background: T.ragRedBg, border: `1px solid ${T.ragRedBorder}`, borderRadius: 10, padding: "10px 14px", fontSize: T.fsSm, color: T.ragRed }}>
          <span>{error}</span>
          <button type="button" data-testid="capacity-retry" onClick={load} style={{ marginLeft: "auto", border: `1px solid ${T.ragRedBorder}`, background: T.card, borderRadius: 7, padding: "4px 10px", cursor: "pointer", color: T.ragRed, fontFamily: "inherit" }}>Retry</button>
        </div>
      )}

      <SidePanelHost>
        <div data-testid="capacity-card" style={{ ...LIST_PRESENTATION.container, flex: 1, minWidth: 0, padding: "10px 12px", boxSizing: "border-box" }}>
          {!data && !error && <Skeleton />}
          {data && (
            <>
              <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: INK, margin: "2px 0 6px" }}>
                {scenarioOn ? "Scenario" : state.view === "quarterly" ? "Next 36 months \u00B7 by calendar quarter" : "Next 36 months"}
                <span style={{ fontWeight: T.fwRegular, color: T.mid }}>{scenarioOn ? " vs baseline \u00B7 outlined squares changed" : " \u00B7 includes committed changes \u25C6"}</span>
                <span style={{ fontWeight: T.fwRegular, color: T.mid }}> · {FORECAST_FADE_LABEL}</span>
              </div>
              {data.markets.length === 0
                ? <div data-testid="capacity-empty-population" style={{ padding: "28px 8px", fontSize: T.fsSm, color: T.mid }}>No active markets yet. Markets appear here once they are active and have offices with demand or usable seats.</div>
                : <CapacityGrid rows={rows} periods={periods} months={months} view={state.view} focusIdx={focusIdx}
                  onFocus={setFocus} openIds={state.open} onToggleOpen={toggleOpen} scenarioOn={scenarioOn}
                  nameWidth={nameColumnWidth(rows)} compact={compact} />}
            </>
          )}
        </div>
        {panelOpen ? (
          <CapacityScenarioPanel levers={state.levers} onChange={(levers) => update({ levers })}
            onReset={() => update({ levers: { ...NEUTRAL_LEVERS } })} onClose={() => setPanelOpen(false)}
            policy={policy} policyError={policyError} canEditPolicy={currentRole === "admin"} result={result} />
        ) : <SidePanelRailButton testId="capacity-scenario-rail" ariaLabel="Open scenario panel" onClick={() => setPanelOpen(true)}
          icon={<SlidersHorizontal size={16} aria-hidden="true" />} label="Scenario" />}
      </SidePanelHost>

      {adding && (
        <CapacityAddMarketDialog onClose={() => setAdding(false)}
          onCreated={() => { setAdding(false); showToast("Market created", { type: "success" }); load(); }} />
      )}
    </div>
  );
}

export default CapacityPage;
